import React from 'react';

import { useClassnames } from '@lumapps/classnames';
import { useDataAttributes } from '@lumapps/data-attributes';
import { Menubar } from '@lumapps/lumx-menu/components/Menubar';
import { mdiDotsHorizontal } from '@lumapps/lumx/icons';
import { type Size, ThemeProvider } from '@lumapps/lumx/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import { ToolbarItem as ToolbarItemType } from '../../types';
import { ToolbarItem } from '../Toolbar/ToolbarItem';

import './index.scss';

interface ElementToolbarProps {
    className?: string;
    toolbarOptions: ToolbarItemType[];
    staticOptions?: ToolbarItemType[];
    toolbarAriaLabel: string;
    dataScope: string;
    currentBreakpoint: 'reduced' | 'full';
    menuBarRef?: React.RefObject<HTMLDivElement>;
    size?: Extract<Size, 's' | 'm'>;
    shouldBeDisplayedOutsideElement?: boolean;
}

const CLASSNAME = 'wrex-element-toolbar';

export const ElementToolbar = ({
    className,
    toolbarOptions,
    staticOptions,
    toolbarAriaLabel,
    dataScope,
    currentBreakpoint,
    menuBarRef,
    size,
    shouldBeDisplayedOutsideElement = false,
}: ElementToolbarProps) => {
    const { get: getDataAttributes } = useDataAttributes(dataScope);
    const { translateKey } = useTranslate();

    const { block, element } = useClassnames(CLASSNAME);

    let visibleItems = toolbarOptions;
    let overflowItems: ToolbarItemType[] = [];

    if (currentBreakpoint === 'reduced') {
        overflowItems = toolbarOptions;
        visibleItems = [];
    }

    return (
        <div
            className={block(
                {
                    'has-static-options': !!staticOptions && staticOptions?.length > 0,
                    'has-outside-placement': shouldBeDisplayedOutsideElement,
                },
                [className],
            )}
            contentEditable={false}
            ref={menuBarRef}
        >
            <ThemeProvider value={undefined}>
                <Menubar ariaLabel={toolbarAriaLabel} size={size}>
                    {visibleItems.map((item) => (
                        <ToolbarItem
                            {...item}
                            otherProps={{
                                ...item.otherProps,
                            }}
                            key={`toolbar-item-${item.itemKey}`}
                        />
                    ))}

                    {overflowItems.length > 0 && (
                        <Menubar.ItemSubmenu
                            variant="icon"
                            icon={mdiDotsHorizontal}
                            label={translateKey(GLOBAL.MORE)}
                            {...getDataAttributes({ element: 'button', action: 'more-button' })}
                            className={element('submenu')}
                        >
                            {overflowItems.map((item) => {
                                return <ToolbarItem {...item} inVerticalMode key={`toolbar-item-${item.itemKey}`} />;
                            })}
                        </Menubar.ItemSubmenu>
                    )}
                    {staticOptions?.map((item) => (
                        <ToolbarItem
                            {...item}
                            otherProps={{
                                ...item.otherProps,
                            }}
                            key={`toolbar-item-${item.itemKey}`}
                        />
                    ))}
                </Menubar>
            </ThemeProvider>
        </div>
    );
};
